import type {
  OcppMessageEntry,
  OcppStartProblem,
  TransactionLinkingProblem,
} from "../../../services/client/types.gen";

export const OCPP_ACTIONS = [
  "Authorize",
  "BootNotification",
  "ChangeAvailability",
  "ChangeConfiguration",
  "ClearChargingProfile",
  "DataTransfer",
  "DiagnosticsStatusNotification",
  "FirmwareStatusNotification",
  "GetConfiguration",
  "GetVariables",
  "Heartbeat",
  "MeterValues",
  "NotifyEvent",
  "NotifyReport",
  "RemoteStartTransaction",
  "RemoteStopTransaction",
  "RequestStartTransaction",
  "RequestStopTransaction",
  "Reset",
  "SecurityEventNotification",
  "SetChargingProfile",
  "SetVariables",
  "StartTransaction",
  "StatusNotification",
  "StopTransaction",
  "TransactionEvent",
  "TriggerMessage",
  "UnlockConnector",
];

export const SENDER_LABELS: Record<OcppMessageEntry["sender"], string> = {
  charging_pole: "Charger → CPMS",
  cpms: "CPMS → Charger",
};

export const MESSAGE_TYPE_LABELS: Record<
  OcppMessageEntry["message_type"],
  string
> = {
  call: "Request",
  call_result: "Response",
  call_error: "Error",
  unparsable: "Unparsable",
};

export const MESSAGE_TYPE_COLORS: Record<
  OcppMessageEntry["message_type"],
  string
> = {
  call: "blue",
  call_result: "green",
  call_error: "red",
  unparsable: "volcano",
};

type ProblemDescription = { label: string; description: string };

export const TRANSACTION_PROBLEMS: Record<
  TransactionLinkingProblem,
  ProblemDescription
> = {
  broker_not_in_linking_mode: {
    label: "Broker in smart mode",
    description:
      "The OCPP broker for this site runs in smart mode: OCPP creates its own transactions and MQTT transactions are never linked to a badge.",
  },
  transaction_start_unknown: {
    label: "No start time",
    description:
      "The transaction has no start time, so there is nothing to match an OCPP start against.",
  },
  asset_not_mapped: {
    label: "Asset not mapped",
    description:
      "No OCPP connector is mapped to this asset. Fix the socket-to-asset map in the OCPP Broker tab.",
  },
  no_ocpp_start_found: {
    label: "No OCPP start received",
    description:
      "The broker received no StartTransaction or TransactionEvent (Started) on this site around the transaction start. Check that the charger is connected through the broker.",
  },
};

export const START_PROBLEMS: Record<OcppStartProblem, ProblemDescription> = {
  socket_not_mapped: {
    label: "Charger not mapped",
    description:
      "This charger has no entry in the socket-to-asset map, so the start cannot be tied to an asset.",
  },
  connector_missing: {
    label: "No connector",
    description:
      "The start message has no connectorId / evse.id, so the broker skips it.",
  },
  connector_not_mapped: {
    label: "Connector not mapped",
    description:
      "The socket-to-asset map has fewer assets than this connector number.",
  },
  different_asset: {
    label: "Other asset",
    description:
      "This connector maps to a different asset than the transaction. Either the session happened on another connector or the socket-to-asset map is wrong.",
  },
  id_token_missing: {
    label: "No badge",
    description:
      "The start message carries no idTag / idToken, so there is no badge to link.",
  },
  no_response: {
    label: "No response",
    description:
      "No StartTransaction response from the CPMS was seen. OCPP 1.6 starts are only linked once the CPMS returns the transactionId.",
  },
  response_is_error: {
    label: "CPMS error",
    description:
      "The CPMS answered the start with a CallError, so the broker never published it.",
  },
  charger_timestamp_invalid: {
    label: "Bad timestamp",
    description:
      "The charger's timestamp in the start message is missing or invalid; matching uses that timestamp.",
  },
  outside_match_window: {
    label: "Too far apart",
    description:
      "The charger's start time is further from the MQTT start time than the matching window allows. Usually the charger clock is off or the session started on another transaction.",
  },
};

const SUMMARY_FIELDS: { label: string; read: (payload: Payload) => unknown }[] =
  [
    { label: "connector", read: (payload) => payload.connectorId },
    { label: "evse", read: (payload) => nested(payload.evse, "id") },
    { label: "event", read: (payload) => payload.eventType },
    { label: "idTag", read: (payload) => payload.idTag },
    { label: "idToken", read: (payload) => nested(payload.idToken, "idToken") },
    { label: "transactionId", read: (payload) => payload.transactionId },
    {
      label: "transactionId",
      read: (payload) => nested(payload.transactionInfo, "transactionId"),
    },
    { label: "status", read: (payload) => payload.status },
    {
      label: "status",
      read: (payload) =>
        nested(payload.idTagInfo, "status") ??
        nested(payload.idTokenInfo, "status"),
    },
    { label: "errorCode", read: (payload) => payload.errorCode },
    { label: "reason", read: (payload) => payload.reason },
    { label: "meterStart", read: (payload) => payload.meterStart },
    { label: "meterStop", read: (payload) => payload.meterStop },
  ];

type Payload = Record<string, unknown>;

const nested = (value: unknown, key: string): unknown =>
  value !== null && typeof value === "object"
    ? (value as Payload)[key]
    : undefined;

export const summarisePayload = (payload: Payload | null | undefined) =>
  SUMMARY_FIELDS.flatMap(({ label, read }) => {
    const value = payload ? read(payload) : undefined;
    return value === undefined || value === null || typeof value === "object"
      ? []
      : [{ label, value: String(value) }];
  });

export const formatDuration = (durationSeconds: number) => {
  const totalSeconds = Math.round(Math.abs(durationSeconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const padded = (value: number) => String(value).padStart(2, "0");
  return hours > 0
    ? `${hours}h ${padded(minutes)}m ${padded(seconds)}s`
    : `${minutes}m ${padded(seconds)}s`;
};

export const formatOffset = (offsetSeconds: number) =>
  `${offsetSeconds < 0 ? "−" : "+"}${formatDuration(offsetSeconds)}`;

export const MESSAGE_TIME_FORMAT = "DD MMM HH:mm:ss.SSS";
